import { useClassName } from './useClassName';
import type { SxProps } from './sxProps';
import { palette } from './palette';
import { styleRule } from './styleRule';
import type { ButtonBaseProps, ButtonElementProps } from './Button';

/**
 * - `plain`: transparent, takes the surrounding text color.
 * - `solid`: charcoal disc.
 * - `glass`: translucent white disc for use over video.
 */
export type IconButtonTone = 'plain' | 'solid' | 'glass';
export type IconButtonSize = 'small' | 'medium' | 'large';

export type IconButtonProps = Omit<ButtonBaseProps, 'aria-label'> &
  ButtonElementProps & {
    'aria-label': string;
    tone?: IconButtonTone;
    size?: IconButtonSize;
  };

const iconBaseSx: SxProps = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  margin: 0,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'background-color 0.2s, color 0.2s, width 0.2s, height 0.2s',
  '& svg': { display: 'block' },
  '&:focus-visible': {
    outline: `3px solid ${palette.teal}`,
    outlineOffset: '2px',
  },
};

const iconToneSx: Record<IconButtonTone, SxProps> = {
  plain: {
    backgroundColor: 'transparent',
    color: 'inherit',
    '&:hover': { backgroundColor: 'rgba(127, 127, 127, 0.12)' },
  },
  solid: {
    backgroundColor: palette.charcoal,
    color: palette.cream,
    '&:hover': { backgroundColor: palette.vermilion },
  },
  glass: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    color: '#fff',
    '&:hover': { backgroundColor: palette.charcoal },
  },
};

const iconSizeSx: Record<IconButtonSize, SxProps> = {
  small: { width: '38px', height: '38px' },
  medium: { width: '40px', height: '40px' },
  large: { width: '76px', height: '76px' },
};

export function IconButton(props: IconButtonProps) {
  const { tone = 'plain', size = 'medium', children, href, type, disabled, ...rest } = props;
  const { className, css, styleId } = useClassName(
    [iconToneSx[tone], iconSizeSx[size]],
    iconBaseSx,
  );

  return (
    <>
      {styleRule(styleId, css)}
      {href !== undefined ? (
        <a {...rest} href={href} className={className}>
          {children}
        </a>
      ) : (
        <button {...rest} type={type ?? 'button'} disabled={disabled} className={className}>
          {children}
        </button>
      )}
    </>
  );
}
